import { analytics } from '@helpers/analytics'
import { getUserDataFromAnalytics } from '@helpers/session/user'
import { getCookieByName } from '@helpers/session'
import {
  LEG,
  CUT,
  SHORTSLEG,
  SIZE,
  LENGTH,
  PRODUCT_PAGE_BREAKDOWN_MERGE_TYPES,
  PRODUCT_COLOUR_TAGS,
  SWIMSHORTSLEG,
  FULFILLMENT_LOCATION_FLAG,
  MIX_AND_MATCH_PDP_TAG,
  PRODUCT_TYPE_TAGS
} from '@consts/index'
import { removeQueryParamsFromUrl, toTitleCase } from '@helpers/utils'
import { getNativeCurrencyCode } from '@helpers/env'
import { CustomAttribute } from 'types/checkout'
import colours from '@styles/core/helpers/colors.module.scss'
import { Fit } from 'types/fit'
import { FulfillmentLocation } from 'types/product'

export function getProductSkuProductId({ sku }: { sku: string | undefined }) {
  return sku?.trim().slice(0, 14)
}

export function getProductSkuGroupId({ sku }: { sku: string | undefined }) {
  return sku?.trim().slice(0, 6)
}

export function getProductSku16({ sku }: { sku: string | undefined }) {
  return sku?.trim().slice(0, 16)
}

export function getProductFamily(productCategory: string) {
  if (
    productCategory.includes('Tops') ||
    productCategory.includes('The Jack')
  ) {
    return 'Tops'
  }

  if (
    productCategory.includes('Gift') ||
    productCategory.includes('Accessories')
  ) {
    return 'Accessories'
  }

  return 'Bottoms'
}

// TODO: Remove this function when we have a better and unified way to determine product category
export const getProductCategory = (
  options: { type: string }[],
  productType?: string | undefined
): string | null => {
  let category = null
  if (!!options) {
    options.forEach(option => {
      switch (option.type.toLowerCase()) {
        case LEG.toLowerCase():
        case 'lower leg':
        case 'taper':
          category = 'Trousers'
          break

        case SHORTSLEG.toLowerCase():
        case SWIMSHORTSLEG.toLowerCase():
          category = 'Shorts'
          break

        case CUT.toLowerCase():
        case SIZE.toLowerCase():
        case LENGTH.toLowerCase():
          category = 'Tops'
          break

        default:
          category = 'Accessories'
      }
    })
  }

  if (!!productType) {
    if (productType.includes('Trunks')) {
      category = 'Underwear'
    } else if (productType.includes('Denim') && category !== 'Tops') {
      category = 'Denim'
    } else if (productType.includes('Gift')) {
      category = 'Gift Card'
    } else if (productType.includes('Swims')) {
      category = 'Shorts'
    } else if (productType.includes('Socks') || productType.includes('Belts')) {
      category = 'Accessories'
    } else if (productType.includes('The Jack')) {
      category = 'The Jack'
    }
  }

  return category
}

export function mergeProductType(
  productType: string
): keyof typeof PRODUCT_PAGE_BREAKDOWN_MERGE_TYPES | string {
  const pType = Object.keys(PRODUCT_PAGE_BREAKDOWN_MERGE_TYPES).map(key => {
    const mergeType =
      PRODUCT_PAGE_BREAKDOWN_MERGE_TYPES[
        key as keyof typeof PRODUCT_PAGE_BREAKDOWN_MERGE_TYPES
      ]
    if (mergeType?.includes(productType)) {
      return key
    }
  })
  return pType[0] || productType
}

export function selectedFitEvent(
  optionType: string,
  currentOptionValue: string,
  extraProps?: { fulfillment?: string } & Record<string, string>
) {
  const pageType = /(collections)/i.test(window.location.href)
    ? 'Collection'
    : 'Product'
  const props = {
    category: `${pageType} Page`,
    label: `Product option (${optionType} - ${currentOptionValue})`,
    url: window.location.href,
    ...extraProps
  }
  // Add a custom property to product filters event ONLY
  if (pageType === 'Product') props.fulfillment = 'Normal'
  analytics('Click', props)
}

type CommonEventProduct = {
  url?: string
  product_feed?: {
    brand?: string
    category?: string
    product_id?: string
    group_id?: string
  }
  title?: string
  product_type?: string
  sku?: string
  sku16?: string | null
  price?: string
}

type CommonEventProductProperties = {
  brand: string
  category?: string
  color?: string
  currency: string
  name?: string
  product_id: string | undefined
  price: number | null
  group_id: string | undefined
  sku16: string | undefined
  url: string
}

export const facebookPixelId = getCookieByName('_fbp') || ''
export const facebookAdClickId = getCookieByName('_fbc') || ''

export function commonEventProductProperties(
  product: CommonEventProduct
): CommonEventProductProperties {
  const formmattedUrl = removeQueryParamsFromUrl(
    product.url || window.location.href
  )
  return {
    brand: product.product_feed?.brand || 'SPOKE',
    category: product.product_feed?.category,
    color: product.title,
    currency: getNativeCurrencyCode(),
    name: product.product_type,
    product_id:
      product.product_feed?.product_id ||
      getProductSkuProductId({ sku: product.sku }),
    price: Number(product.price) || null,
    group_id:
      product.product_feed?.group_id ||
      getProductSkuGroupId({ sku: product.sku }),
    sku16: product.sku16 || getProductSku16({ sku: product.sku }),
    url: formmattedUrl
  }
}

export function getProductVariantAsString(
  properties: Record<string, string | number>
): string | null {
  if (
    properties === null ||
    properties === undefined ||
    Object.keys(properties).length === 0
  ) {
    return null
  }

  return Object.keys(properties)
    .map(key => properties[key])
    .filter(v => v)
    .join(' / ')
}

export function productEventUserProperties() {
  const { traits } = getUserDataFromAnalytics()
  return traits
}

export function productViewedEvent(
  product: CommonEventProduct & { image: { src: string } },
  savedFit: Fit | null,
  options?: Record<string, unknown>
) {
  const commonProps = commonEventProductProperties(product)
  const { email, firstName, phone } = productEventUserProperties()
  const props = {
    ...commonProps,
    ...options,
    image_url: product.image.src,
    ...(!!facebookPixelId && { fbp: facebookPixelId }),
    ...(!!facebookAdClickId && { fbc: facebookAdClickId }),
    saved_fit: !!savedFit,
    ...{ email, firstName, phone }
  }
  analytics('Product Viewed', props)
}

export function productSkuViewedEvent(
  product: CommonEventProduct & { image: { src: string } },
  savedFit: Fit | null,
  options?: Record<string, unknown>
) {
  const commonProps = commonEventProductProperties(product)
  const props = {
    ...commonProps,
    ...options,
    image_url: product.image.src,
    ...(!!facebookPixelId && { fbp: facebookPixelId }),
    ...(!!facebookAdClickId && { fbc: facebookAdClickId }),
    ...productEventUserProperties(),
    saved_fit: !!savedFit
  }
  analytics('Product SKU Viewed', props)
}

export type ProductListViewedArgs = {
  products: {
    quantity: number
    name: string
    color: string
    price: number
    position: number
    url: string
    image_url: string
    brand: string
  }[]
  context: string
  savedFit: Fit | null
  listId?: string
  listName?: string
  [key: string]: unknown
}
export function productListViewed({
  products,
  context,
  listId,
  listName,
  savedFit,
  ...rest
}: ProductListViewedArgs) {
  analytics(
    'Product List Viewed',
    {
      context,
      products: products.slice(0, 10),
      ...(listId && { list_id: listId }),
      ...(listName && { list_name: listName }),
      saved_fit: !!savedFit,
      ...rest
    },
    {
      integrations: {
        All: true,
        'Facebook Pixel': false
      }
    }
  )
}

export const getCareCopyProductTypeLabel = (productType: string) => {
  if (productType.toLowerCase().includes('merino')) {
    return 'merino'
  }

  if (productType.toLowerCase().includes('denim')) {
    return 'denim'
  }

  if (productType.toLowerCase().includes('polo')) {
    return 'polo'
  }

  if (productType.toLowerCase().includes('socks')) {
    return 'socks'
  }

  return productType.toLowerCase()
}

function getProductTagValue(tagName: string, tag = '') {
  return tag?.replace(`${tagName}:`, '').trim()
}

const tagsMap = {
  beta: 'Beta',
  new: 'New',
  preorder: 'Pre-order',
  'new season': 'New Season',
  'low stock': 'Low Stock',
  organic: 'Organic',
  'out-of-stock': 'Out of stock'
} as const

function formatTag(tag: string): string {
  const tagFormatted = tag.toLowerCase().trim()

  switch (true) {
    case tagFormatted.includes('promo'):
      return toTitleCase(getProductTagValue('promo', tag)) ?? tag

    case tagFormatted.includes('pdp'):
      return toTitleCase(getProductTagValue('pdp', tag)) ?? tag

    case tagFormatted.includes('auto-discount'):
      return `${getProductTagValue('auto-discount', tag)} off`

    case tagFormatted.includes('betawear'):
      return 'Beta'

    case tagFormatted.includes('organic'):
      return 'Organic'

    case tagFormatted.includes('new product'):
      return 'New'

    case tagFormatted.includes('fabric'):
      return tagsMap[
        getProductTagValue('fabric', tagFormatted) as keyof typeof tagsMap
      ]

    case tagFormatted.includes('priority'):
      return getProductTagValue('priority', tagFormatted) ?? tag

    case tagFormatted.includes('limited-edition'):
      return 'Limited Edition'

    default:
      return tagsMap[tagFormatted as keyof typeof tagsMap] ?? tag
  }
}

type Tags = string[]
type ValidTags = readonly string[] | undefined

function cleanProductTag(tag: string): string {
  return tag.trim().toLowerCase().replace(/\s+/g, ' ').replace(/\s+:/g, ':')
}

function normalizeProductTag(tag: string): string {
  return cleanProductTag(tag).replace(/:\s*/g, ':')
}

function prioritizeBadgeTags(tags: Tags): Tags {
  const priorityTags = tags.filter(
    tag => tag.startsWith('promo:') || tag.startsWith('pdp:')
  )
  const otherTags = tags.filter(
    tag => !tag.startsWith('promo:') && !tag.startsWith('pdp:')
  )

  return [...priorityTags, ...otherTags]
}

function getFilteredTags(tags: Tags) {
  const cleanedTags = tags.map(cleanProductTag)

  const tagsWithAutomaticDiscount = cleanedTags.filter(tag =>
    tag.includes('auto-discount')
  )
  if (tagsWithAutomaticDiscount.length > 0) {
    const tagsWithoutAutomaticDiscount = cleanedTags.filter(
      tag => !tag.includes('auto-discount')
    )

    const tagsSortedByLowerDiscountAmount = tagsWithAutomaticDiscount.sort()

    const automaticDiscountTag = tagsSortedByLowerDiscountAmount[0]

    const sortedTags = [automaticDiscountTag, ...tagsWithoutAutomaticDiscount]

    return prioritizeBadgeTags(sortedTags)
  }

  return prioritizeBadgeTags(cleanedTags)
}

function findProductTag(tags: Tags, validTags: ValidTags) {
  return findProductTags(tags, validTags)[0]
}

function findProductTags(tags: Tags, validTags: ValidTags) {
  const filterTags = getFilteredTags(tags)

  return filterTags.filter(tag => {
    const normalizedTag = normalizeProductTag(tag)

    return validTags?.some(validTag =>
      normalizedTag.includes(normalizeProductTag(validTag))
    )
  })
}

type FormattedTag = { raw: string; formatted: string } | null

export function getProductTag(
  tags: Tags | undefined = [],
  validTags: ValidTags = []
): { raw: string; formatted: string } | null {
  if (!tags) return null

  const productTag = findProductTag(tags, validTags)

  return productTag
    ? { raw: productTag, formatted: formatTag(productTag) }
    : null
}

/**
 * Every product type tag that should render as a PDP badge, ordered with
 * `promo:`/`pdp:` tags first. These tags are display-only, so a product showing
 * an unwanted badge should have the tag removed in Shopify rather than being
 * filtered here.
 */
export function getProductTypeTags(
  tags: Tags | undefined = []
): NonNullable<FormattedTag>[] {
  const seenLabels = new Set<string>()

  return findProductTags(tags, PRODUCT_TYPE_TAGS)
    .map(tag => ({ raw: tag, formatted: formatTag(tag) }))
    .filter(({ formatted }) => {
      // Valid tags are matched as substrings, so distinct tags can share a
      // label — `organic` and `tax:organic` both render as "Organic".
      if (seenLabels.has(formatted)) return false

      seenLabels.add(formatted)

      return true
    })
}

export function getProductColourTag(tags: Tags | undefined = []) {
  const colourTags = tags.filter(
    tag => !normalizeProductTag(tag).endsWith('mix & match')
  )

  return getProductTag(colourTags, PRODUCT_COLOUR_TAGS)
}

export function combineVariantsInventoryLevel(
  variants: { inventory_quantity: number }[] = []
) {
  if (!variants) return 0

  return variants.reduce(
    (total, variant) => total + variant.inventory_quantity,
    0
  )
}

type ProductCartItemsArgs = {
  customAttributes: CustomAttribute[]
  quantity?: number
  variantId: string
  fulfillmentLocation?: FulfillmentLocation
}

export function getProductCartItems({
  quantity = 1,
  customAttributes,
  variantId,
  fulfillmentLocation
}: ProductCartItemsArgs): {
  quantity: number
  merchandiseId: string
  attributes: CustomAttribute[]
}[] {
  return [
    {
      quantity,
      merchandiseId: variantId,
      attributes: [
        ...customAttributes,
        { key: '_addedToCartTimestamp', value: `${Date.now()}` },
        ...(fulfillmentLocation
          ? [{ key: FULFILLMENT_LOCATION_FLAG, value: fulfillmentLocation }]
          : [])
      ]
    }
  ]
}

export function getBadgeTheme(tag: FormattedTag): string {
  if (!tag?.formatted || !tag?.raw) {
    return colours.colorPrimary
  }

  const tagToLowerCase = tag.formatted.toLowerCase().trim()

  switch (true) {
    case normalizeProductTag(tag.raw) === MIX_AND_MATCH_PDP_TAG:
      return colours.colorPrimary

    // Promotions
    case tag?.raw.includes('promo'):
      return colours.colorDanger

    // Positive indicators
    case tagToLowerCase.includes('organic'):
    case tagToLowerCase.includes('new season'):
    case tagToLowerCase.includes('new'):
    case tag?.raw.includes('priority'):
      return colours.colorSuccess

    // Price/discount/sale-related
    case tag?.raw.includes('discount'):
    case tag?.raw.includes('sale'):
    case tag?.raw.includes('% off'):
      return colours.colorDanger

    // Special status
    case tag?.raw.includes('beta'):
    case tag?.raw.includes('limited edition'):
    case tag?.raw.includes('pre-order'):
    case tag?.raw.includes('auto-discount'):
      return colours.colorPrimary

    // Scarcity
    case tagToLowerCase.includes('low stock'):
      return colours.colorWarning

    default:
      return colours.colorPrimary
  }
}
